import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "./TopBar";
import { useDispatch, useSelector } from "react-redux";
import { getCartTotal } from "../redux/CartSlice";
import Category from "./Category";
import { nav } from "../data/Data";
import "../css/components.css";

/**
 * One header bar.
 *
 * This used to render two stacked bars — a cream "top bar" (wordmark, a second
 * centred logo, account icon) above a brown nav bar (categories, links, cart).
 * Two headers for one site, with a white seam between them and the categories
 * button breaking the bar's height. Now a single row:
 *
 *   [ wordmark ] [ Browse Categories | nav links ] [ cart · account ]
 */
export default function Header() {
  const dispatch = useDispatch();
  const { totalItems, totalAmounts } = useSelector((state) => state.cart);
  const [isSticky, setIsSticky] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    dispatch(getCartTotal());
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScroll = () => {
    setIsSticky(window.scrollY > 100);
  };

  return (
    <header className={`nt-header ${isSticky ? "is-sticky" : ""}`}>
      <div className="nt-header-inner">
        {/* Brand */}
        {/* Wordmark only.
            The circular wreath mark was tried here and pulled back out: it's a
            badge with fine leaf detail and its own lettering, which turns to
            mush at 38px, the source art is only 67x100, and its green/pink
            palette fights the warm brand system. It belongs somewhere it has
            room — see the About-style block or a redrawn version. */}
        <Link to="/" className="nt-wordmark" aria-label="NT Sticks home">
          <span className="nt-wordmark-nt">NT</span>
          <span className="nt-wordmark-sticks">Sticks</span>
        </Link>

        {/* Categories + primary nav */}
        <div className="nt-header-nav">
          <Category />
          <nav className="nt-nav-links" aria-label="Primary">
            {nav.slice(0, 4).map((list, key) => (
              <Link
                to={list.path}
                className="nt-nav-link"
                key={key}
                onClick={() => setMenuOpen(false)}
              >
                {list.text}
              </Link>
            ))}
          </nav>
        </div>

        {/* Cart + account.
            Icons are drawn inline to a shared 1.7 stroke weight — the previous
            pair mixed a Font Awesome glyph with a heavy filled person.svg, so
            they never sat together. The cart also shows its running subtotal,
            which is what shoppers actually want to check. */}
        <div className="nt-header-actions">
          <Link
            to="/cart"
            className={`nt-cart ${totalItems > 0 ? "has-items" : ""}`}
            aria-label={`Cart, ${totalItems} item${totalItems === 1 ? "" : "s"}`}
          >
            <span className="nt-cart-icon">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M3 4h2.2a1 1 0 0 1 .98.8l2.34 11.2a1 1 0 0 0 .98.8h8.9a1 1 0 0 0 .97-.76L21 9H6.4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="10" cy="20" r="1.4" fill="currentColor" />
                <circle cx="17.5" cy="20" r="1.4" fill="currentColor" />
              </svg>
              {totalItems > 0 && <span className="nt-cart-badge">{totalItems}</span>}
            </span>
            <span className="nt-cart-meta">
              <em>Cart</em>
              <strong>₹{totalAmounts || 0}</strong>
            </span>
          </Link>

          <TopBar isSticky={isSticky} />

          <button
            type="button"
            className="nt-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-controls="nt-mobile-nav"
            aria-label="Toggle navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        id="nt-mobile-nav"
        className={`nt-mobile-nav ${menuOpen ? "is-open" : ""}`}
      >
        {/* Single wrapper on purpose. The drawer animates with the
            grid-template-rows 0fr->1fr technique, which only sizes the first
            row — with the links as direct children, HOME took the animated row
            and collapsed to a clipped sliver while the other three sat at full
            height as implicit auto rows. */}
        <div className="nt-mobile-nav-inner">
          {nav.slice(0, 4).map((list, key) => (
            <Link
              to={list.path}
              className="nt-mobile-link"
              key={key}
              onClick={() => setMenuOpen(false)}
            >
              {list.text}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}
